import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { getFormattedAnswers, isSessionExpired } from "@/utils";
import appConfig from "@/config/app-config";
import { Answers, FormattedAnswer } from "@/data/onboarding/types";
import { FlowType } from "./onboardingReducer";

type WebOnboardingRegistrationSubmission = {
  treatmentArea: string;
  answers: FormattedAnswer[];
};

type ContextValue = {
  saveAnswers: (
    answers: Answers,
    treatmentArea?: FlowType,
    height?: number,
    weight?: number
  ) => Promise<Response>;
};

export const ApiContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const ApiProvider = ({ children }: React.PropsWithChildren) => {
  const { session } = useAuth();

  const saveAnswers = (
    answers: Answers,
    treatmentArea?: FlowType,
    height?: number,
    weight?: number
  ) => {
    if (!session?.jwt || isSessionExpired(session)) {
      return Promise.reject(new Error("No valid session present"));
    }

    const formattedAnswers = getFormattedAnswers(answers);

    if (treatmentArea === "obesity") {
      if (height) {
        formattedAnswers.push({
          questionId: 125,
          answer: [Math.floor(height).toString()],
        });
      }

      if (weight) {
        formattedAnswers.push({
          questionId: 435,
          answer: [weight.toString()],
        });
      }
    }

    const submission: WebOnboardingRegistrationSubmission = {
      treatmentArea: treatmentArea?.toUpperCase() || "OBESITY",
      answers: formattedAnswers,
    };

    return fetch(`${appConfig.apiBase}/web-onboarding/register`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.jwt}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submission),
    });
  };

  const value = {
    saveAnswers,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};
